import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  TextField,
} from "@mui/material";
import localize from "../../../common/localize";
import { Add } from "@mui/icons-material";
import { useEffect, useState } from "react";
import NoDataPage from "../../../components/noDataPage/NoDataPage";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getClearGeneralRequestNote } from "../../../common/EntitiesManagement";
import ModalBackground from "../../../components/common/ModalBackground";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import LoadingPanel from "../../../components/common/LoadingPanel";
import ReviewCard from "../../event/components/ReviewCard";

export default function RequestNotes({ requestModel }) {
  const { config, GENERAL_REQUESTS_ENDPOINT, createEntity } =
    useGlobalContext();
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState(getClearGeneralRequestNote());
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getNotes = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get(
        `${GENERAL_REQUESTS_ENDPOINT}/${requestModel?.Id}/note`,
        {
          ...config,
          Pagination: -1,
        }
      );

      if (
        result?.data?.Ok &&
        result?.data?.Data &&
        result?.data?.Data?.length > 0
      ) {
        setNotes([...result.data.Data]);
      }
    } catch (exception) {
      console.error("Error loading request notes: ", exception);
    } finally {
      setIsLoading(false);
    }
  };

  const addNote = () => {
    setNote(getClearGeneralRequestNote());
    setShowAddModal(true);
  };

  const handleChange = (e) => {
    setNote({
      ...note,
      [e.target.name]: e.target.value,
    });
  };

  const createNote = async (noteModel) => {
    const result = await createEntity(
      `${GENERAL_REQUESTS_ENDPOINT}/${noteModel?.Id}/note`,
      {
        ...noteModel,
        GeneralRequestId: requestModel?.Id,
      },
      config
    );

    validateOperationResult(result, false);
  };

  const validateOperationResult = (result, isDelete) => {
    setNote(getClearGeneralRequestNote());

    if (result.ok) {
      getNotes();
    }
  };

  const showAddNoteModal = () => {
    return (
      <ModalBackground
        title={localize.add_note}
        showModal={showAddModal}
        setShowModal={setShowAddModal}
        onClickToCancel={() => {
          setNote(getClearGeneralRequestNote());
          setShowAddModal(false);
        }}
        onClickToSubmit={() => {
          createNote(note);
          setShowAddModal(false);
        }}
        validators={!note.Note || note.Note?.length <= 0}
        Component={
          <Box sx={{ width: 450 }} className="my-2">
            <FormControl fullWidth>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={note?.Note}
                onChange={handleChange}
                size="small"
                fullWidth
                multiline
                rows={2}
                id="Note"
                label={localize.note}
                name="Note"
                variant="outlined"
              />
            </FormControl>
          </Box>
        }
      />
    );
  };

  const getUI = () => {
    if (notes && notes?.length > 0) {
      return (
        <Box sx={{ width: "100%" }} className="flex flex-col gap-2">
          {showAddModal && showAddNoteModal()}

          <Box className="w-full flex gap-2 items-center">
            <Alert severity="info" sx={{ width: "100%" }}>
              <label className="text-xs">
                {localize.request_notes_description}
              </label>
            </Alert>

            {requestModel?.Status <= 1 && (
              <Button
                sx={{ width: "150px" }}
                size="medium"
                variant="contained"
                color="success"
                className="app_general_button_data app_success_button btn-sm "
                onClick={() => {
                  addNote();
                }}
              >
                <Add style={{ width: "16px", height: "16px" }} />
                <div className="mr-1" />
                {localize.add}
              </Button>
            )}
          </Box>

          <Divider />

          <Box className="flex flex-col gap-2" sx={{ overflowY: "auto" }}>
            {notes.map((item) => (
              <Box key={item?.Id}>
                <ReviewCard
                  review={{
                    Title: new Date(item?.CreationDate).toLocaleString(
                      undefined,
                      {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }
                    ),
                    Observation: item.Note,
                    CreatedBy: item.CreatedBy,
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        {showAddModal && showAddNoteModal()}

        <NoDataPage
          pageTitle={localize.no_notes}
          noHeight
          showAddButton
          onAddButtonClick={() => {
            addNote();
          }}
        />
      </Box>
    );
  };

  useEffect(() => {
    getNotes();
  }, []);

  return isLoading ? (
    <Box sx={{ width: "100%" }}>
      <LoadingPanel donotUseHscreen />
    </Box>
  ) : (
    getUI()
  );
}
